import { ChevronDown } from "lucide-react";
import dayjs from "dayjs";

import { cn } from "@/lib/utils";
import { useInterests } from "@/hooks/queries/useInterests";
import { useProfileItems } from "@/hooks/queries/useProfileItems";
import { useDepartments } from "@/hooks/queries/useDepartments";
import type { Department, SearchProfile } from "@/types/domain";

/** Dónde busca la línea: el departamento si es uno, el conteo si son varios. */
function locationLabel(
  departmentIds: number[],
  departments: Department[],
): string {
  if (departmentIds.length === 0) return "Todo el Perú";
  if (departmentIds.length > 1) return `${departmentIds.length} departamentos`;
  const match = departments.find((d) => d.department_id === departmentIds[0]);
  return match?.department_name.toUpperCase() ?? "1 departamento";
}

/** Ventana de publicación de la línea, en corto. */
function dateLabel(from?: string | null, to?: string | null): string {
  const short = (value: string) => dayjs(value).format("DD MMM");
  if (from && to) return `${short(from)} – ${short(to)}`;
  if (from) return `Desde ${short(from)}`;
  if (to) return `Hasta ${short(to)}`;
  return "Todo el tiempo";
}

/**
 * Cabecera plegable de móvil: resume la línea de negocio activa y abre el panel
 * de líneas, recursos, ítems y filtros. Cerrada por defecto para que los
 * resultados queden al alcance sin scrollear toda la configuración.
 */
export function MobileProfileToggle({
  profile,
  open,
  onToggle,
}: {
  profile: SearchProfile;
  open: boolean;
  onToggle: () => void;
}) {
  const profileId = profile.searchprofile_id;
  // Las mismas queries que usa el panel: se leen de caché, no piden red extra.
  const { data: interests = [] } = useInterests(profileId);
  const { data: items = [] } = useProfileItems(profileId);
  const { data: departments = [] } = useDepartments();

  const summary = [
    // En una línea con el análisis activo los recursos no cuentan (ver `ProfileCard`).
    !profile.searchprofile_checkqualification &&
      `${interests.filter((i) => i.active ?? true).length} recursos`,
    `${items.filter((i) => i.active ?? true).length} ítems`,
    locationLabel(profile.searchprofile_departmentids ?? [], departments),
    dateLabel(profile.searchprofile_date_from, profile.searchprofile_date_to),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={cn(
        "flex w-full items-center gap-3 rounded-2xl border bg-card px-4 py-3 text-left shadow-sm transition-colors lg:hidden",
        open ? "border-primary" : "border-transparent",
      )}
    >
      <span className="size-2 shrink-0 rounded-full bg-primary" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold">
          {profile.searchprofile_name}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {summary}
        </span>
      </span>
      <ChevronDown
        className={cn(
          "size-4 shrink-0 text-muted-foreground transition-transform",
          open && "rotate-180",
        )}
      />
    </button>
  );
}
